import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import PropTypes from 'prop-types'

import ErrorFallback from 'components/Shared/ErrorFallback'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'
import { ChevronIcon, RightArrowIcon } from 'images'
import { objectToParams, returnErrorMessage } from 'utils/helpers'
import constants, { EXCLUDED_TEST_CAMPAIGN_PARTNERS } from 'constants'
import authApi from 'api/auth'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { CopyPrompt } from 'components/Settings/Meta/Connect.jsx'
import BrandingSettings from 'components/Settings/Branding/BrandingSettings'
import CustomCheckBox from 'components/Shared/CustomCheckBox'

import {
  GoogleConnectContextProvider,
  googleActions,
  googleSteps,
  isIdentityVerificationOk,
  useGoogleConnectContext,
  useGoogleConnectDispatchContext,
} from './connect'
import Modal from 'components/Shared/Modal'
import Portal from 'components/Shared/Portal'
import { SecondaryButton } from 'components/Shared/Button'
import {
  GoogleTestCampaignProvider,
  GoogleTestCampaignAutoPromptModal,
  GoogleTestCampaignModal,
  GoogleTestCampaignButton,
  useGoogleTestCampaignContext,
} from './TestCampaign'

// ─── Google G icon ──────────────────────────────────────────────────────────────

export function GoogleGIcon({ size = 24 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

// ─── Account Picker ────────────────────────────────────────────────────────────

function AccountPickerRow({ account, onSelect, loading }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const isActive = account.account_status === 'enabled'

  if (account.already_connected) {
    return (
      <div className="google-account-picker__item google-account-picker__item--disabled">
        <div className="flex-grow-1">
          <p className="google-account-picker__name">{account.descriptive_name}</p>
          {(account.currency_code || account.time_zone) && (
            <p className="google-account-picker__sub">
              {[account.currency_code, account.time_zone].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <span className="asset__active-label asset__active-label--active">
          {t('Settings.GoogleAccess.accountSelection.alreadyConnected')}
        </span>
      </div>
    )
  }

  if (!isActive) {
    return (
      <div>
        <button
          className="google-account-picker__item google-account-picker__item--disabled"
          onClick={() => setExpanded((v) => !v)}
        >
          <div>
            <p className="google-account-picker__name">{account.descriptive_name}</p>
            {(account.currency_code || account.time_zone) && (
              <p className="google-account-picker__sub">
                {[account.currency_code, account.time_zone].filter(Boolean).join(' · ')}
              </p>
            )}
            <span className="asset__active-label mt-1 d-inline-block">
              {t('Settings.GoogleAccess.accountSelection.notEnabled')}
            </span>
          </div>
        </button>
        {expanded && (
          <div className="google-account-picker__item-detail">
            <p className="mb-1">
              {t('Settings.GoogleAccess.accountSelection.notEnabledDescription')}
            </p>
            <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer">
              {t('Settings.GoogleAccess.accountSelection.continueSetup')} &rarr;
            </a>
          </div>
        )}
      </div>
    )
  }

  return (
    <button
      className="google-account-picker__item"
      onClick={() => onSelect(account)}
      disabled={loading}
    >
      <div className="flex-grow-1">
        <p className="google-account-picker__name">{account.descriptive_name}</p>
        {(account.currency_code || account.time_zone) && (
          <p className="google-account-picker__sub">
            {[account.currency_code, account.time_zone].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      {loading ? (
        <LoadingSpinner size={SpinnerSize.SMALL} showText={false} />
      ) : (
        <ChevronIcon className="google-account-picker__chevron" />
      )}
    </button>
  )
}

function AccountPickerContent({ accounts, onSelect, loading, modalError }) {
  const { t } = useTranslation()

  return (
    <>
      <h1>{t('Settings.GoogleAccess.accountSelection.title')}</h1>
      <p className="body-txt light larger mb-4 text-left">
        {t('Settings.GoogleAccess.accountSelection.description')}
      </p>
      {modalError && (
        <div className="alert alert-danger mb-4">
          {modalError.localizedMessage ?? t(returnErrorMessage(modalError))}
        </div>
      )}
      <div className="ad-platform ad-platform_dialog-content">
        <div className="google-account-picker__list">
          {accounts.map((account) => (
            <AccountPickerRow
              key={account.customer_id}
              account={account}
              onSelect={onSelect}
              loading={loading}
            />
          ))}
        </div>
      </div>
    </>
  )
}

// ─── Invitation Pending ─────────────────────────────────────────────────────────

export function InvitationPendingContent({
  accountName,
  customerId,
  invitationStatus,
  invitationTimedOut,
  billingValid,
  accountStatus,
  identityVerificationStatus,
  identityVerificationActionUrl,
  modalError,
  onRetry,
  onDone,
}) {
  const { t } = useTranslation()

  const accountInfo = (accountName || customerId) && (
    <p className="mb-3">
      {accountName}
      {customerId && <span className="text-muted ms-1 small">· {customerId}</span>}
    </p>
  )

  const renderIdentityVerificationAlert = () => {
    if (identityVerificationStatus === 'PENDING_REVIEW') {
      return (
        <div className="alert alert-info mb-0">
          Your identity verification is under review by Google. Ads may be paused until it
          completes.
        </div>
      )
    }

    if (
      identityVerificationStatus === 'PENDING_USER_ACTION' ||
      identityVerificationStatus === 'UNSPECIFIED'
    ) {
      return (
        <div className="alert alert-warning mb-0 d-flex flex-column gap-1">
          <p className="mb-0 font-weight-bold">
            {t('Settings.GoogleAccess.connected.identityVerificationRequired')}
          </p>
          <p className="mb-0">
            {t('Settings.GoogleAccess.connected.identityVerificationDescription')}
            {identityVerificationActionUrl ? ' ' : ''}
            {identityVerificationActionUrl && (
              <a href={identityVerificationActionUrl} target="_blank" rel="noopener noreferrer">
                {t('Settings.GoogleAccess.connected.completeVerification')} &rarr;
              </a>
            )}
          </p>
        </div>
      )
    }

    return null
  }

  if (modalError) {
    return (
      <>
        <h1>{t('Settings.GoogleAccess.invitationPending.title')}</h1>
        {accountInfo}
        <div className="ad-platform ad-platform_dialog-content d-flex flex-column gap-3">
          <div className="alert alert-danger mb-0">
            {modalError.localizedMessage ?? t(returnErrorMessage(modalError))}
          </div>
          <SecondaryButton
            text={t('Settings.GoogleAccess.invitationPending.retry')}
            onClick={onRetry}
            color="navy"
            fullWidth={false}
            className="align-self-start"
          />
        </div>
      </>
    )
  }

  if (invitationStatus === 'ACTIVE' && accountStatus === 'enabled') {
    const identityVerificationAlert = renderIdentityVerificationAlert()

    return (
      <>
        <h1>{t('Settings.GoogleAccess.connected.title')}</h1>
        {accountInfo}
        <div className="ad-platform ad-platform_dialog-content d-flex flex-column gap-3 align-items-center">
          <p className="mb-0 text-center">
            {t('Settings.GoogleAccess.connected.successDescription')}
          </p>
          {billingValid === false && (
            <p className="body-txt light mb-0">
              {t('Settings.GoogleAccess.connected.billingNudge')}{' '}
              <a
                href="https://ads.google.com/aw/billing/summary"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Settings.GoogleAccess.connected.setupBilling')} &rarr;
              </a>
            </p>
          )}
          {identityVerificationAlert}
          <SecondaryButton
            text={t('Settings.GoogleAccess.connected.done')}
            onClick={onDone}
            color="orange"
            fullWidth={false}
          />
        </div>
      </>
    )
  }

  if (invitationStatus === 'ACTIVE' && accountStatus === 'not_enabled') {
    return (
      <>
        <h1>{t('Settings.GoogleAccess.accountActivation.title')}</h1>
        {accountInfo}
        <div className="ad-platform ad-platform_dialog-content d-flex flex-column gap-3">
          <p className="body-txt light mb-0">
            {t('Settings.GoogleAccess.accountActivation.description')}
          </p>
          <div className="d-flex align-items-center gap-2">
            <LoadingSpinner size={SpinnerSize.SMALL} showText={false} />
            <span className="body-txt light">
              {t('Settings.GoogleAccess.accountActivation.polling')}
            </span>
          </div>
          <p className="body-txt light small mb-0">
            {t('Settings.GoogleAccess.accountActivation.note')}
          </p>
        </div>
      </>
    )
  }

  if (invitationStatus === 'REFUSED') {
    return (
      <>
        <h1>{t('Settings.GoogleAccess.invitationPending.title')}</h1>
        {accountInfo}
        <div className="ad-platform ad-platform_dialog-content d-flex flex-column gap-3">
          <div className="alert alert-danger mb-0">
            {t('Settings.GoogleAccess.invitationPending.refused')}
          </div>
          <SecondaryButton
            text={t('Settings.GoogleAccess.invitationPending.retry')}
            onClick={onRetry}
            color="navy"
            fullWidth={false}
            className="align-self-start"
          />
        </div>
      </>
    )
  }

  if (invitationTimedOut) {
    return (
      <>
        <h1>{t('Settings.GoogleAccess.invitationPending.title')}</h1>
        {accountInfo}
        <div className="ad-platform ad-platform_dialog-content d-flex flex-column gap-3">
          <div className="alert alert-warning mb-0">
            {t('Settings.GoogleAccess.invitationPending.timedOut')}
          </div>
          <SecondaryButton
            text={t('Settings.GoogleAccess.invitationPending.retry')}
            onClick={onRetry}
            color="navy"
            fullWidth={false}
            className="align-self-start"
          />
        </div>
      </>
    )
  }

  return (
    <>
      <h1>{t('Settings.GoogleAccess.invitationPending.title')}</h1>
      {accountInfo}
      <div className="ad-platform ad-platform_dialog-content d-flex flex-column gap-3">
        <p className="mb-0">{t('Settings.GoogleAccess.invitationPending.instructions')}</p>

        <ul className="steps-list">
          {[1, 2, 3, 4].map((stepIdx) => (
            <li key={stepIdx} className="d-flex align-items-center gap-2">
              <span className="steps-name">
                {t(`Settings.GoogleAccess.invitationPending.checkEmailSteps.${stepIdx}`)}
              </span>

              {stepIdx < 4 && <RightArrowIcon width={12} height={12} className="steps-arrow" />}
            </li>
          ))}
        </ul>

        <div className="d-flex align-items-center gap-2">
          <LoadingSpinner size={SpinnerSize.SMALL} showText={false} />
          <span className="body-txt light">
            {t('Settings.GoogleAccess.invitationPending.polling')}
          </span>
        </div>
      </div>
    </>
  )
}

// ─── Connect Modal (unified account picker + invitation pending) ──────────────

function ConnectModal({
  step,
  accounts,
  selectedAccountName,
  selectedCustomerId,
  invitationStatus,
  invitationTimedOut,
  billingValid,
  accountStatus,
  identityVerificationStatus,
  identityVerificationActionUrl,
  onSelectAccount,
  onRetry,
  onDone,
  loading,
  modalError,
  onClose,
}) {
  const [displayedStep, setDisplayedStep] = useState(step)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (displayedStep === step) {
      setVisible(true)
      return
    }
    setVisible(false)
    const timer = setTimeout(() => setDisplayedStep(step), 150)
    return () => clearTimeout(timer)
  }, [step, displayedStep])

  const isTerminal =
    invitationStatus === 'ACTIVE' || invitationStatus === 'REFUSED' || invitationTimedOut
  const pendingClose = invitationStatus === 'ACTIVE' ? onDone : isTerminal ? onRetry : undefined
  const isPending = step === googleSteps.INVITATION_PENDING
  const onClickOutside = isPending ? pendingClose : loading ? undefined : onClose

  return (
    <Portal wrapperId="app-portal">
      <Modal
        isOpen={true}
        onClickOutside={onClickOutside}
        closeCallback={onClickOutside}
        offWhiteBg
        size="md"
        fullWidth={false}
        mainContent={
          <div
            className="ad-platform ad-platform_dialog"
            style={{ transition: 'opacity 0.15s ease', opacity: visible ? 1 : 0 }}
          >
            {displayedStep === googleSteps.INVITATION_PENDING ? (
              <InvitationPendingContent
                accountName={selectedAccountName}
                customerId={selectedCustomerId}
                invitationStatus={invitationStatus}
                invitationTimedOut={invitationTimedOut}
                billingValid={billingValid}
                accountStatus={accountStatus}
                identityVerificationStatus={identityVerificationStatus}
                identityVerificationActionUrl={identityVerificationActionUrl}
                modalError={modalError}
                onRetry={onRetry}
                onDone={onDone}
              />
            ) : (
              <AccountPickerContent
                accounts={accounts}
                onSelect={onSelectAccount}
                loading={loading}
                modalError={modalError}
              />
            )}
          </div>
        }
      />
    </Portal>
  )
}

// ─── Connected Assets ───────────────────────────────────────────────────────────

function ConnectedAssets({ assets, onToggle, onDisconnect }) {
  const { t } = useTranslation()
  // const { connections } = useGoogleConnectContext()

  if (!assets.length) {
    return (
      <div className="settings-page__section">
        <p className="body-txt light mb-0">{t('Settings.GoogleAccess.assets.noAssets')}</p>
      </div>
    )
  }

  return (
    <>
      {assets.map((asset) => {
        const status = asset.extra_data?.invitation_status
        const pixels = asset.pixels ?? []
        const isActive = status === 'ACTIVE' && asset.extra_data?.account_status === 'enabled'
        // const usernames = connections
        //   .filter((c) => asset.asset_users?.some((u) => u.platform_user_id === c.platform_user_id))
        //   .map((c) => c.email)
        //   .filter(Boolean)

        return (
          <div key={asset.id} className="settings-page__section ad-platform_assets_asset border-0">
            <div className="settings-page__sectionBody">
              <div className="settings-page__sectionWrapper">
                <div className="settings-page__sectionState">
                  <div className="d-flex align-items-center gap-2">
                    <CustomCheckBox
                      checked={asset.is_enabled}
                      onChange={(e) => onToggle(asset.id, e.target.checked)}
                    />

                    <div>
                      <span style={{ display: 'inline-block', transform: 'translateY(2px)' }}>
                        {asset.asset_label} (ID: {asset.asset_id})
                      </span>
                      {(asset.extra_data?.currency_code || asset.extra_data?.time_zone) && (
                        <p className="mb-0 body-txt light small">
                          {[asset.extra_data.currency_code, asset.extra_data.time_zone]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </div>

                    <span
                      className={cn('asset__active-label align-self-baseline', {
                        'asset__active-label--active': isActive,
                      })}
                      style={{
                        lineHeight: '1',
                        paddingTop: '5px',
                        display: 'inline-block',
                        transform: 'translateY(1px)',
                      }}
                    >
                      {isActive
                        ? t('common.active')
                        : t(`Settings.GoogleAccess.assets.invitationStatus.${status}`, status)}
                    </span>
                  </div>
                </div>
                <div className="settings-page__sectionColumn">
                  <SecondaryButton
                    text={t('Settings.GoogleAccess.disconnect.title')}
                    onClick={() => onDisconnect(asset.asset_id)}
                    color="navy"
                    size="small"
                    fullWidth={false}
                  />
                </div>
              </div>
              {/* {usernames.length > 0 && (
                <div className="settings-page__sectionColumn asset__usernames gap-1">
                  <div className="asset__usernames__label">
                    {t('Settings.MetaAccess.Assets.connectedBy')}:{' '}
                  </div>
                  {usernames.map((username) => (
                    <div className="asset__usernames__item" key={username}>
                      {username}
                    </div>
                  ))}
                </div>
              )} */}
            </div>
            {(pixels.length > 0 || status === 'ACTIVE') && (
              <div className="ms-3 d-flex flex-column gap-1 pt-2">
                {pixels.map((pixel) => (
                  <div
                    key={pixel.id}
                    className="d-flex align-items-center gap-2 body-txt light small"
                  >
                    <span className="asset__active-label asset__active-label--active">
                      {t('Settings.GoogleAccess.assets.conversionLabel')}
                    </span>
                    <span>{pixel.asset_label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

// ─── Main Content ───────────────────────────────────────────────────────────────

function GoogleAccessContent({ isAnonymous = false }) {
  const { t } = useTranslation()
  const popupRef = useRef(null)
  const errorRef = useRef(null)
  const { addNotification } = useToast()

  const {
    loading,
    error,
    modalError,
    step,
    partnerConnectionType,
    accounts,
    selectedCustomerId,
    selectedAccountName,
    invitationStatus,
    invitationTimedOut,
    billingValid,
    accountStatus,
    identityVerificationStatus,
    identityVerificationActionUrl,
    assets,
    isAnonymous: isAnonymousCtx,
  } = useGoogleConnectContext()

  const { dispatch } = useGoogleConnectDispatchContext()

  const partnerId = useSelector((state) => state.user).partnerId
  const isTestCampaignExcluded = EXCLUDED_TEST_CAMPAIGN_PARTNERS.includes(partnerId)

  const isConnectedCleanly =
    invitationStatus === 'ACTIVE' &&
    accountStatus === 'enabled' &&
    billingValid !== false &&
    isIdentityVerificationOk(identityVerificationStatus)

  const [code, setCode] = useState('')
  const [promptOpen, setPromptOpen] = useState(false)
  const [fetchingCode, setFetchingCode] = useState(false)

  useEffect(() => {
    if (isAnonymous) {
      dispatch({ type: googleActions.SET_ANONYMOUS })
    }
  }, [dispatch, isAnonymous])

  // Load initial state on mount
  useEffect(() => {
    dispatch({ type: googleActions.GET_STATUS })
    // For anonymous: GET_ASSETS is deferred until GET_STATUS completes (needs platform_user_id)
    if (!isAnonymous) {
      dispatch({ type: googleActions.GET_ASSETS })
    }
  }, [dispatch])

  // Resume polling if a pending invitation is found in assets after mount
  useEffect(() => {
    if (!assets.length || step !== googleSteps.IDLE) return
    const pendingAsset = assets.find((a) => a.extra_data?.invitation_status === 'PENDING')
    if (pendingAsset) {
      dispatch({
        type: googleActions.SET_SELECTED_ACCOUNT,
        payload: { customerId: pendingAsset.asset_id, accountName: pendingAsset.asset_label },
      })
      dispatch({ type: googleActions.SET_STEP, payload: googleSteps.INVITATION_PENDING })
      dispatch({
        type: googleActions.POLL_INVITATION,
        payload: { customerId: pendingAsset.asset_id },
      })
    }
  }, [assets, step, dispatch])

  // Scroll to error
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [error])

  // Listen for Google OAuth popup messages
  useEffect(() => {
    const handler = (event) => {
      if (event.data.type !== 'google_callback') return

      popupRef.current?.close()
      popupRef.current = null

      if (event.data.code) {
        dispatch({
          type: googleActions.AUTHORIZE,
          payload: {
            code: event.data.code,
            redirectUri: `${window.location.origin}/g-callback`,
          },
        })
      } else {
        dispatch({
          type: googleActions.SET_ERROR,
          payload: { localizedMessage: t('Settings.GoogleAccess.loginCancelled') },
        })
      }
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [dispatch, t])

  const openGoogleOAuth = useCallback(() => {
    const redirectUri = `${window.location.origin}/g-callback`
    const state = `google_${Math.random().toString(36).substring(7)}`
    sessionStorage.setItem('googleAuthSession', JSON.stringify({ state, redirectUri }))

    const oauthUrl =
      'https://accounts.google.com/o/oauth2/auth' +
      objectToParams({
        client_id: constants.GOOGLE_ADS_CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope:
          'https://www.googleapis.com/auth/adwords openid https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        access_type: 'offline',
        prompt: 'consent',
        state,
      })

    const width = 600
    const height = 700
    const left = (window.innerWidth - width) / 2
    const top = (window.innerHeight - height) / 2
    const popupParams = `width=${width},height=${height},left=${left},top=${top},popup=1,scrollbars=1,menubar=0,toolbar=0,status=0,resizable=1`
    const popup = window.open(oauthUrl, 'googleOAuthWindow', popupParams)

    if (!popup) {
      dispatch({
        type: googleActions.SET_ERROR,
        payload: { localizedMessage: t('Settings.GoogleAccess.popupBlocked') },
      })
    } else {
      popup.focus()
      popupRef.current = popup
    }
  }, [dispatch, t])

  // Refresh billing status when user returns to tab after visiting Google Ads
  useEffect(() => {
    if (
      step !== googleSteps.INVITATION_PENDING ||
      invitationStatus !== 'ACTIVE' ||
      billingValid !== false ||
      !selectedCustomerId
    )
      return

    const handler = () => {
      if (document.visibilityState === 'visible') {
        dispatch({
          type: googleActions.REFRESH_BILLING,
          payload: { customerId: selectedCustomerId },
        })
      }
    }

    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [step, invitationStatus, billingValid, selectedCustomerId, dispatch])

  // Skip the connected modal straight to the test campaign flow when there's nothing
  // (billing/identity) left to show — notify instead
  useEffect(() => {
    if (step !== googleSteps.INVITATION_PENDING || !isConnectedCleanly) return

    addNotification({
      variant: 'success',
      message: t('Toasts.Google.connected.message', 'Google Ads Connected'),
      description: t(
        'Toasts.Google.connected.description',
        'Your Google Ads account has been successfully connected.',
      ),
    })
    dispatch({ type: googleActions.SET_STEP, payload: googleSteps.IDLE })
    dispatch({
      type: googleActions.SET_INVITATION,
      payload: {
        invitationStatus: null,
        billingValid: null,
        accountStatus: null,
        identityVerificationStatus: null,
        identityVerificationActionUrl: null,
      },
    })
    dispatch({ type: googleActions.SET_JUST_SETTLED, payload: true })
  }, [step, isConnectedCleanly, addNotification, t, dispatch])

  const getAnonymousAuthCode = useCallback(() => {
    setFetchingCode(true)
    authApi
      .getAnonymousAuthCode()
      .then((response) => {
        setCode(response.data.code)
        setPromptOpen(true)
      })
      .catch((error) => {
        console.error(error)
        addNotification({
          variant: 'error',
          message: t('Toasts.Google.anonymousCodeFailed.message'),
          description: t('Toasts.Google.anonymousCodeFailed.description'),
        })
      })
      .finally(() => {
        setFetchingCode(false)
      })
  }, [addNotification, t])

  const onSelectAccount = useCallback(
    (account) => {
      dispatch({
        type: googleActions.SELECT_ACCOUNT,
        payload: {
          customer_id: account.customer_id,
          descriptive_name: account.descriptive_name,
          currency_code: account.currency_code,
          time_zone: account.time_zone,
        },
      })
    },
    [dispatch],
  )

  const onCloseNoAccounts = useCallback(() => {
    dispatch({ type: googleActions.CLEAR_MODAL_ERROR })
    dispatch({ type: googleActions.SET_STEP, payload: googleSteps.IDLE })
  }, [dispatch])

  const onRetry = useCallback(() => {
    dispatch({ type: googleActions.CLEAR_MODAL_ERROR })
    dispatch({ type: googleActions.SET_STEP, payload: googleSteps.IDLE })
    dispatch({
      type: googleActions.SET_INVITATION,
      payload: {
        invitationStatus: null,
        billingValid: null,
        accountStatus: null,
        identityVerificationStatus: null,
        identityVerificationActionUrl: null,
      },
    })
  }, [dispatch])

  const onDone = useCallback(() => {
    dispatch({ type: googleActions.SET_STEP, payload: googleSteps.IDLE })
    dispatch({
      type: googleActions.SET_INVITATION,
      payload: {
        invitationStatus: null,
        billingValid: null,
        accountStatus: null,
        identityVerificationStatus: null,
        identityVerificationActionUrl: null,
      },
    })
    if (invitationStatus === 'ACTIVE') {
      dispatch({ type: googleActions.SET_JUST_SETTLED, payload: true })
    }
  }, [dispatch, invitationStatus])

  const onDisconnect = useCallback(
    (customerId = null) => {
      dispatch({ type: googleActions.DISCONNECT, payload: { customerId } })
    },
    [dispatch],
  )

  const onToggleAsset = useCallback(
    (assetId, enabled) => {
      dispatch({ type: googleActions.TOGGLE_ASSET, payload: { assetId, enabled } })
    },
    [dispatch],
  )

  const { showAutoPrompt, handleCloseAutoPrompt, handleAcceptAutoPrompt } =
    useGoogleTestCampaignContext()

  const isConnected = assets.length > 0
  const isDisconnected = partnerConnectionType === 'disconnected'

  // ── Error alert ──
  const errorAlert = error && (
    <div className="mt-3 d-flex flex-column gap-2 mb-2" ref={errorRef}>
      <p className="alert alert-danger mb-0 d-flex justify-content-between align-items-center">
        {error.localizedMessage ??
          (error.noAccounts ? t('Settings.GoogleAccess.noAccounts') : t(returnErrorMessage(error)))}
      </p>
    </div>
  )

  const isInvitationPending = step === googleSteps.INVITATION_PENDING
  const isPickingAccount = step === googleSteps.ACCOUNT_SELECTION
  const isNoAccounts = step === googleSteps.NO_ACCOUNTS
  const connectButtonBusy = loading || isPickingAccount || isNoAccounts || isInvitationPending

  // ── Idle / connected step ──
  return (
    <>
      <div className="ad-platform_connection card card-default settings-page__card">
        <div className="settings-page__section title-bar" style={{ borderBottom: 'none' }}>
          {isConnected ? (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="settings-page__sectionTitle">
                  {t('Settings.GoogleAccess.title')}
                </span>
                {connectButtonBusy && <LoadingSpinner size={SpinnerSize.SMALL} showText={false} />}
              </div>
              <div className="d-flex align-items-center gap-2">
                {!isAnonymousCtx && !isTestCampaignExcluded && (
                  <GoogleTestCampaignButton assets={assets} disabled={connectButtonBusy} />
                )}
                {!isAnonymousCtx && (
                  <SecondaryButton
                    onClick={getAnonymousAuthCode}
                    text={t('Settings.GoogleAccess.anonymous.getLink')}
                    fullWidth={false}
                    size="small"
                    disabled={connectButtonBusy || fetchingCode}
                    classNames="btn-copy"
                    disabledTitle=""
                  />
                )}
                <button
                  className="btn-google-connect"
                  onClick={openGoogleOAuth}
                  disabled={connectButtonBusy}
                >
                  {t('Settings.GoogleAccess.connectAdAccount')}
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div>
                  <p className="mb-1 font-weight-bold">
                    {t('Settings.GoogleAccess.connectPrompt')}
                  </p>
                  <p className="mb-0 body-txt light small">
                    {t('Settings.GoogleAccess.connectDescription')}
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {connectButtonBusy && <LoadingSpinner size={SpinnerSize.SMALL} showText={false} />}
                {!isAnonymousCtx && (
                  <SecondaryButton
                    onClick={getAnonymousAuthCode}
                    text={t('Settings.GoogleAccess.anonymous.getLink')}
                    fullWidth={false}
                    size="small"
                    disabled={connectButtonBusy || fetchingCode}
                    classNames="btn-copy"
                    disabledTitle=""
                  />
                )}
                <button
                  className="btn-google-connect"
                  onClick={openGoogleOAuth}
                  disabled={connectButtonBusy}
                >
                  {isDisconnected
                    ? t('Settings.GoogleAccess.reconnect')
                    : t('Settings.GoogleAccess.connect')}
                </button>
              </div>
            </div>
          )}
          {errorAlert}
        </div>

        {isConnected && (
          <>
            <ConnectedAssets assets={assets} onToggle={onToggleAsset} onDisconnect={onDisconnect} />

            <div
              style={{ borderTop: '1px solid #eaeaea', marginTop: '12px', marginBottom: '12px' }}
            />

            <BrandingSettings />
          </>
        )}
      </div>
      {(isPickingAccount || (isInvitationPending && !isConnectedCleanly)) && (
        <ConnectModal
          step={step}
          accounts={accounts}
          selectedAccountName={selectedAccountName}
          selectedCustomerId={selectedCustomerId}
          invitationStatus={invitationStatus}
          invitationTimedOut={invitationTimedOut}
          billingValid={billingValid}
          accountStatus={accountStatus}
          identityVerificationStatus={identityVerificationStatus}
          identityVerificationActionUrl={identityVerificationActionUrl}
          onSelectAccount={onSelectAccount}
          onRetry={onRetry}
          onDone={onDone}
          loading={loading}
          modalError={modalError}
          onClose={onCloseNoAccounts}
        />
      )}
      {isNoAccounts && (
        <Portal wrapperId="app-portal">
          <Modal
            isOpen={true}
            onClickOutside={onCloseNoAccounts}
            closeCallback={onCloseNoAccounts}
            offWhiteBg
            size="lg"
            fullWidth={false}
            mainContent={
              <div className="ad-platform ad-platform_dialog">
                <h1>{t('Settings.GoogleAccess.noAccountsTitle')}</h1>
                <p className="body-txt light larger mb-4 mt-4">
                  {t('Settings.GoogleAccess.noAccounts')}
                </p>
                <SecondaryButton
                  className="bt ml-auto"
                  text={t('common.close')}
                  fullWidth
                  onClick={onCloseNoAccounts}
                />
              </div>
            }
          />
        </Portal>
      )}
      {!isAnonymousCtx && !isTestCampaignExcluded && (
        <GoogleTestCampaignAutoPromptModal
          isOpen={showAutoPrompt}
          onClose={handleCloseAutoPrompt}
          onAccept={handleAcceptAutoPrompt}
        />
      )}
      {!isAnonymousCtx && !isTestCampaignExcluded && <GoogleTestCampaignModal assets={assets} />}
      <CopyPrompt
        isOpen={promptOpen}
        code={code}
        path="google-anonymous"
        onClose={() => setPromptOpen(false)}
      />
    </>
  )
}

// ─── Test Campaign Wrapper ──────────────────────────────────────────────────────
// Bridge: reads justSettled from connect context, provides test campaign context

function GoogleTestCampaignWrapper({ isAnonymous }) {
  const { justSettled } = useGoogleConnectContext()
  const { dispatch } = useGoogleConnectDispatchContext()

  const onSettledConsumed = useCallback(
    () => dispatch({ type: googleActions.SET_JUST_SETTLED, payload: false }),
    [dispatch],
  )

  return (
    <GoogleTestCampaignProvider justSettled={justSettled} onSettledConsumed={onSettledConsumed}>
      <GoogleAccessContent isAnonymous={isAnonymous} />
    </GoogleTestCampaignProvider>
  )
}

GoogleTestCampaignWrapper.propTypes = {
  isAnonymous: PropTypes.bool,
}

export default function GoogleAccess({ isAnonymous = false }) {
  return (
    <div className="ad-platform">
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <GoogleConnectContextProvider>
          <GoogleTestCampaignWrapper isAnonymous={isAnonymous} />
        </GoogleConnectContextProvider>
      </ErrorBoundary>
    </div>
  )
}

GoogleAccess.propTypes = {
  isAnonymous: PropTypes.bool,
}
